import { doc, setDoc, collection, addDoc, serverTimestamp, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';
import toast from 'react-hot-toast';

let functions = null;
let httpsCallable = null;

try {
    const functionsModule = require('firebase/functions');
    const configModule = require('../firebase/config');
    functions = configModule.functions;
    httpsCallable = functionsModule.httpsCallable;
} catch (error) { }

export const paymentService = {

    async calculateShipping(country, items) {

        const shippingRates = {
            'GB': 1.99,
            'PT': 5.99,
            'ES': 5.99,
            'DE': 6.99,
            'FR': 6.99,
            'IT': 9.99,
            'NL': 8.99,
            'BE': 8.99,
            'AT': 9.99,
            'IE': 4.99,
            'DK': 12.99,
            'SE': 12.99,
            'NO': 15.99,
            'FI': 12.99,
            'PL': 10.99,
            'CZ': 10.99,
            'HU': 10.99,
            'RO': 11.99,
            'BG': 11.99,
            'HR': 11.99,
            'SI': 10.99,
            'SK': 10.99,
            'LT': 11.99,
            'LV': 11.99,
            'EE': 11.99,
            'LU': 8.99,
            'MT': 13.99,
            'CY': 14.99,
            'other': 19.99
        };

        const calculateLocalShipping = () => {
            try {
                const subtotal = Array.isArray(items) ? items.reduce((sum, item) => {
                    const price = parseFloat(item?.price || 0);
                    const quantity = parseInt(item?.quantity || 0);
                    return sum + (price * quantity);
                }, 0) : 0;

                const baseShippingCost = shippingRates[country] || shippingRates['other'];
                const europeanCountries = Object.keys(shippingRates).filter(c => c !== 'other');
                const finalShippingCost = (europeanCountries.includes(country) && subtotal >= 50) ? 0 : baseShippingCost;

                return {
                    shippingCost: finalShippingCost,
                    freeShippingThreshold: 50,
                    isEligibleForFreeShipping: subtotal >= 50 && europeanCountries.includes(country),
                    error: null,
                    source: 'local'
                };
            } catch (error) {
                return {
                    shippingCost: shippingRates[country] || shippingRates['other'] || 9.99,
                    freeShippingThreshold: 50,
                    isEligibleForFreeShipping: false,
                    error: null,
                    source: 'fallback'
                };
            }
        };

        return calculateLocalShipping();
    },

    async testAuthentication() {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            return { authenticated: false, error: 'No user signed in' };
        }

        return {
            authenticated: true,
            error: null,
            userId: user.uid,
            email: user.email
        };
    },

    isPaymentConfigured() {
        const hasStripeKey = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        const hasValidKey = hasStripeKey &&
            process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY !== 'pk_test_your_stripe_publishable_key_here' &&
            (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_') ||
                process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY.startsWith('pk_live_'));

        return hasValidKey;
    },

    async createPaymentIntent(paymentData) {

        if (!this.isPaymentConfigured()) {
            toast.error('Payment system configuration error. Please contact support.');
            return {
                clientSecret: null,
                paymentIntentId: null,
                error: 'Stripe not configured'
            };
        }

        if (!functions || !httpsCallable) {
            toast.error('Payment processing service unavailable. Please try again later.');
            return {
                clientSecret: null,
                paymentIntentId: null,
                error: 'Firebase Functions not available'
            };
        }

        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                throw new Error('Please sign in to complete your purchase');
            }

            await user.getIdToken(true);

            const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
            const result = await createPaymentIntent(paymentData);

            return {
                clientSecret: result.data.clientSecret,
                paymentIntentId: result.data.paymentIntentId,
                orderNumber: result.data.orderNumber,
                error: null
            };
        } catch (error) {
            let errorMessage = 'Failed to initialize payment. Please try again.';

            if (error.code === 'unauthenticated') {
                errorMessage = 'Please sign in to complete your purchase.';
            } else if (error.code === 'permission-denied') {
                errorMessage = 'Permission denied. Please try signing out and back in.';
            } else if (error.code === 'functions/unimplemented') {
                errorMessage = 'Payment service not deployed. Please contact support.';
            } else if (error.code === 'functions/internal') {
                errorMessage = 'Payment service error. Please try again or contact support.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
            return {
                clientSecret: null,
                paymentIntentId: null,
                error: errorMessage
            };
        }
    },

    async refreshAuthentication() {
        try {
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                throw new Error('No user signed in');
            }

            await user.getIdToken(true);

            return { success: true, userId: user.uid };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    async getUserOrders(userId) {
        try {
            const ordersQuery = query(
                collection(db, 'orders'),
                where('userId', '==', userId)
            );
            const querySnapshot = await getDocs(ordersQuery);
            const orders = [];

            querySnapshot.forEach((doc) => {
                orders.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            return { data: orders, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    async cancelOrder(orderId, reason) {
        try {
            const orderRef = doc(db, 'orders', orderId);
            await updateDoc(orderRef, {
                status: 'cancelled',
                cancelledAt: serverTimestamp(),
                cancellationReason: reason || 'Customer requested',
                updatedAt: serverTimestamp()
            });

            toast.success('Order cancelled successfully');
            return { success: true, error: null };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    async createOrder(userId, orderData) {
        try {
            const orderNumber = this.generateOrderNumber();
            const order = {
                userId,
                ...orderData,
                status: 'processing',
                orderNumber: orderNumber,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, 'orders'), order);

            const emptyCart = {
                items: [],
                total: 0,
                updatedAt: new Date().toISOString()
            };
            await setDoc(doc(db, 'cart', userId), emptyCart);

            toast.success('Order created successfully! Check your email for confirmation.');
            return { data: { id: docRef.id, ...order }, error: null };
        } catch (error) {
            toast.error('Error creating order');
            return { data: null, error: error.message };
        }
    },

    generateOrderNumber() {
        const timestamp = Date.now().toString();
        const random = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `AM${timestamp.slice(-6)}${random}`;
    },

    async sendConfirmationEmail(paymentIntentId) {
        if (!functions || !httpsCallable) {
            return { success: false, error: 'Email service not configured' };
        }

        try {
            const sendEmail = httpsCallable(functions, 'sendOrderConfirmationEmail');
            await sendEmail({ paymentIntentId });
            return { success: true, error: null };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    getShippingCountries() {
        return [
            { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
            { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
            { code: 'ES', name: 'Spain', flag: '🇪🇸' },
            { code: 'DE', name: 'Germany', flag: '🇩🇪' },
            { code: 'FR', name: 'France', flag: '🇫🇷' },
            { code: 'IT', name: 'Italy', flag: '🇮🇹' },
            { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
            { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
            { code: 'AT', name: 'Austria', flag: '🇦🇹' },
            { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
            { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
            { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
            { code: 'NO', name: 'Norway', flag: '🇳🇴' },
            { code: 'FI', name: 'Finland', flag: '🇫🇮' },
            { code: 'PL', name: 'Poland', flag: '🇵🇱' },
            { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
            { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
            { code: 'RO', name: 'Romania', flag: '🇷🇴' },
            { code: 'BG', name: 'Bulgaria', flag: '🇧🇬' },
            { code: 'HR', name: 'Croatia', flag: '🇭🇷' },
            { code: 'SI', name: 'Slovenia', flag: '🇸🇮' },
            { code: 'SK', name: 'Slovakia', flag: '🇸🇰' },
            { code: 'LT', name: 'Lithuania', flag: '🇱🇹' },
            { code: 'LV', name: 'Latvia', flag: '🇱🇻' },
            { code: 'EE', name: 'Estonia', flag: '🇪🇪' },
            { code: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
            { code: 'MT', name: 'Malta', flag: '🇲🇹' },
            { code: 'CY', name: 'Cyprus', flag: '🇨🇾' },
            { code: 'other', name: 'Other Countries', flag: '🌍' }
        ];
    },

    calculateTotal(subtotal, shippingCost = 0) {
        return subtotal + shippingCost;
    },

    formatCurrency(amount, currency = 'GBP') {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: currency
        }).format(amount);
    },

    validatePaymentAmount(items, expectedTotal, shippingCost = 0) {
        const calculatedTotal = items.reduce((sum, item) => {
            return sum + (item.price * item.quantity);
        }, 0);

        const totalWithShipping = calculatedTotal + shippingCost;
        const difference = Math.abs(totalWithShipping - expectedTotal);
        return difference < 0.01;
    }
};